import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtUSD, fmtDate, fmtCarat, PARTNER_LABELS } from "@/lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Trash2, Pencil } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { FileUpload } from "@/components/FileUpload";
import { FileGallery } from "@/components/FileGallery";
import SaleActionPanel from "@/components/SaleActionPanel";
import { useAuth } from "@/context/AuthContext";
import { ExpenseSection, JewelryFormDialog } from "@/pages/ParcelDetail";

export default function JewelryDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canWrite } = useAuth();
    const [j, setJ] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);

    async function load() {
        setLoading(true);
        try { const { data } = await api.get(`/jewelry/${id}`); setJ(data); }
        catch { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [id]);

    const remove = async () => {
        if (j.status === "sold") { toast.error("Sold jewelry cannot be deleted"); return; }
        if (!confirm(`Delete "${j.name || j.jewelry_type}"? This also deletes its expenses and returns the source parcel to Available.`)) return;
        try {
            await api.delete(`/jewelry/${j.id}`);
            toast.success("Jewelry deleted");
            navigate("/jewelry");
        } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };

    const changeStatus = async (target) => {
        try {
            await api.patch(`/jewelry/${j.id}/status`, { status: target });
            toast.success(target === "available" ? "Marked ready to sell" : "Moved back into production");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Status change failed"); }
    };

    const updatePhotos = async (photos) => {
        try {
            await api.patch(`/jewelry/${j.id}`, { photos });
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update photos"); }
    };

    if (loading) return <div className="text-stone-500 text-sm">Loading…</div>;
    if (!j) return null;

    const jExp = j.extra_expenses ?? (j.expenses || []).reduce((s, e) => s + e.amount, 0);
    const intrinsic = j.intrinsic_cost ?? 0;
    const pExp = (j.parcel_expenses || []).reduce((s, e) => s + e.amount, 0);
    const totalCost = j.total_cost ?? (j.allocated_cost + intrinsic + jExp);
    const totalBasis = totalCost + pExp;         // full basis for waterfall (includes parcel expenses)
    const sale = (j.sales || [])[0];

    return (
        <div className="space-y-8 animate-fade-up" data-testid="jewelry-detail-page">
            <div className="flex items-center justify-between">
                <Link to="/jewelry" className="text-xs uppercase tracking-widest text-stone-500 hover:text-emerald-700 inline-flex items-center gap-1" data-testid="back-to-jewelry">
                    <ArrowLeft className="h-3 w-3" /> Jewelry
                </Link>
                <button
                    onClick={remove}
                    disabled={j.status === "sold" || !canWrite}
                    className="text-xs uppercase tracking-widest text-stone-500 hover:text-rose-600 inline-flex items-center gap-1 disabled:opacity-30 disabled:hover:text-stone-500"
                    data-testid="delete-jewelry-button"
                    title={j.status === "sold" ? "Sold jewelry cannot be deleted" : (!canWrite ? "Read-only access" : "Delete this jewelry (also deletes its expenses)")}
                >
                    <Trash2 className="h-3 w-3" /> Delete jewelry
                </button>
            </div>

            <div className="space-y-6 animate-fade-up">
                {/* Header */}
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[10px] uppercase tracking-widest text-stone-500">Jewelry</div>
                        <StatusBadge status={j.status} kind="jewelry" testid="jewelry-status-badge" />
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl leading-none mt-2" data-testid="jewelry-title">{j.name || j.jewelry_type}</h1>
                    <div className="text-sm text-stone-500 mt-2">
                        {j.jewelry_type} · created {fmtDate(j.created_at)}
                    </div>
                    <div className="text-sm text-stone-500 mt-1">
                        From parcel <Link to={`/parcels/${j.parcel_id}`} className="font-mono text-stone-700 hover:text-emerald-700 underline decoration-stone-300 hover:decoration-emerald-500" data-testid="jewelry-parcel-link">{j.parcel_snapshot?.code}</Link>
                        {" · "}{fmtCarat(j.parcel_snapshot?.total_carat)} · {j.parcel_snapshot?.clarity} / {j.parcel_snapshot?.color}
                        {j.parcel_snapshot?.number_of_stones ? ` · ${j.parcel_snapshot.number_of_stones} stones` : ""}
                    </div>
                    {j.description && <p className="text-stone-500 mt-4 max-w-2xl text-sm leading-relaxed">{j.description}</p>}
                </div>

                {/* Horizontal stats strip */}
                <div className="border border-stone-200 bg-white grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 divide-x divide-y sm:divide-y-0 divide-stone-200" data-testid="jewelry-info-card">
                    <StatCell label="Total carat" value={fmtCarat(j.parcel_snapshot?.total_carat)} testid="jewelry-total-carat" />
                    <StatCell label="Stones" value={j.parcel_snapshot?.number_of_stones ?? "—"} />
                    <StatCell label="Gold" value={`${j.gold_karat}k ${j.gold_color}`} sub={`${j.gold_weight_gr} g${j.gross_weight_gr != null ? ` · gross ${j.gross_weight_gr} g` : ""}`} testid="jewelry-gold-cell" />
                    <StatCell label="Parcel cost" value={fmtUSD(j.allocated_cost)} testid="jewelry-allocated-cost" />
                    <StatCell label="Gold + labor" value={fmtUSD(intrinsic)} testid="jewelry-intrinsic-cost" sub={intrinsic > 0 && j.production_paid_by ? `paid by ${PARTNER_LABELS[j.production_paid_by]}${j.gold_price_per_oz ? ` · @ ${fmtUSD(j.gold_price_per_oz)}/oz` : ""}` : (intrinsic > 0 ? null : "—")} />
                    <StatCell label="Extra expenses" value={fmtUSD(jExp)} testid="jewelry-expenses-sum" />
                    <StatCell label="Total cost" value={fmtUSD(totalCost)} accent testid="jewelry-total-cost" sub={pExp > 0 ? `+ ${fmtUSD(pExp)} parcel exp.` : null} />
                </div>

                {/* Actions */}
                {j.status !== "sold" && canWrite && (
                    <div className="flex flex-wrap gap-3" data-testid="jewelry-actions">
                        <button
                            onClick={() => setEditOpen(true)}
                            className="border border-stone-300 text-stone-800 text-xs uppercase tracking-widest px-4 py-2 hover:bg-stone-100 transition-colors inline-flex items-center gap-1.5"
                            data-testid="edit-jewelry-button"
                        >
                            <Pencil className="h-3 w-3" /> Edit
                        </button>
                        {j.status === "in_production" && (
                            <button
                                onClick={() => changeStatus("available")}
                                className="bg-emerald-600 text-white text-xs uppercase tracking-widest px-4 py-2 hover:bg-emerald-700 transition-colors"
                                data-testid="mark-ready-button"
                            >
                                Mark ready to sell
                            </button>
                        )}
                        {j.status === "available" && (
                            <button
                                onClick={() => changeStatus("in_production")}
                                className="border border-stone-300 text-stone-700 text-xs uppercase tracking-widest px-4 py-2 hover:bg-stone-100 transition-colors"
                                data-testid="mark-production-button"
                            >
                                Back to production
                            </button>
                        )}
                    </div>
                )}
                <JewelryFormDialog open={editOpen} onOpenChange={setEditOpen} existing={j} onDone={load} />

                {/* Photos */}
                <div className="space-y-3">
                    {(j.photos?.length > 0) && (
                        <FileGallery files={j.photos} label="Photos" testid="jewelry-photos-gallery" />
                    )}
                    {j.status !== "sold" && canWrite && (
                        <div>
                            <FileUpload
                                value={j.photos || []}
                                onChange={updatePhotos}
                                accept="image/*"
                                label={j.photos?.length ? "Add more photos" : "Add photos"}
                                testid="jewelry-add-photos"
                            />
                        </div>
                    )}
                </div>
            </div>

            <Tabs defaultValue="sale">
                <TabsList className="bg-transparent border-b border-stone-200 rounded-none w-full justify-start p-0 h-auto">
                    <TabsTrigger value="sale" data-testid="tab-sale" className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:bg-transparent data-[state=active]:text-stone-900 text-stone-500 uppercase text-xs tracking-widest px-4 py-3">Sale</TabsTrigger>
                    <TabsTrigger value="expenses" data-testid="tab-expenses" className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:bg-transparent data-[state=active]:text-stone-900 text-stone-500 uppercase text-xs tracking-widest px-4 py-3">Expenses</TabsTrigger>
                </TabsList>

                <TabsContent value="sale" className="mt-6">
                    <SaleActionPanel target_type="jewelry" target_id={j.id} existingSale={sale} onChanged={load} />
                </TabsContent>
                <TabsContent value="expenses" className="mt-6 space-y-6">
                    <ExpenseSection targetType="jewelry" targetId={j.id} expenses={j.expenses || []} onChanged={load} />
                    {(j.parcel_expenses || []).length > 0 && (
                        <div>
                            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Inherited from source parcel</div>
                            <div className="border border-stone-200" data-testid="parcel-expenses-inherited">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                                            <th className="py-3 px-4">Date</th>
                                            <th className="py-3 px-4">Category</th>
                                            <th className="py-3 px-4">Paid by</th>
                                            <th className="py-3 px-4 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {j.parcel_expenses.map(e => (
                                            <tr key={e.id} className="border-b border-stone-200/70">
                                                <td className="py-3 px-4 text-stone-500">{fmtDate(e.date)}</td>
                                                <td className="py-3 px-4 text-stone-800">{e.category}</td>
                                                <td className="py-3 px-4 text-stone-700">{PARTNER_LABELS[e.paid_by]}</td>
                                                <td className="py-3 px-4 text-right num text-rose-600">{fmtUSD(e.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}

const Row = ({ label, value, accent, testid }) => (
    <div className="flex items-center justify-between py-1.5 border-b last:border-b-0 border-stone-200/70">
        <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
        <span className={`num ${accent ? "text-emerald-700" : "text-stone-900"}`} data-testid={testid}>{value}</span>
    </div>
);

const StatCell = ({ label, value, sub, accent, testid }) => (
    <div className="px-4 py-3">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className={`num text-base mt-0.5 ${accent === "rose" ? "text-rose-600" : accent ? "text-emerald-700" : "text-stone-900"}`} data-testid={testid}>{value}</div>
        {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
);
